import {
  Stack,
  TextField,
  MenuItem,
  Typography,
  Button,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CacheService } from "../../../../common/services/cache.service";
import APIService from "../../../../services/api.service";
import axios from "../../../../services/axiosInstance";
import environment from "../../../../environment/environment";
import { useRecoilState } from "recoil";
import { tokenState } from "../../../../atoms/token";
import React, { useEffect, useReducer, useState } from "react";
import { Backdrop, CircularProgress } from '@mui/material';

// Reusable report section block
const ReportSection = ({
  title,
  subtitle,
  input,
  useDate,
  onDownload,
  disabled,
}: {
  title: string;
  subtitle?: string;
  input?: React.ReactNode;
  useDate?: React.ReactNode;
  onDownload: () => void;
  disabled?: boolean;
}) => (
  <>
    <Stack spacing={0.5} direction="row" sx={{ mb: 1 }}>
      <Typography variant="h6">{title}</Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ mt: -0.5 }}>
          ({subtitle})
        </Typography>
      )}
    </Stack>
    <Stack spacing={2} direction="row" sx={{ mb: 2 }}>
      <Stack spacing={1} direction="column">
        {input}
      </Stack>
      <Stack spacing={1} direction="column">
        {useDate}
      </Stack>
      <Stack spacing={1} direction="column">
        <Button
          variant="outlined"
          size="small"
          sx={{ minHeight: 40 }}
          startIcon={<FontAwesomeIcon icon={["fas", "arrow-down"]} />}
          onClick={onDownload}
          disabled={disabled}
        >
          Download Report
        </Button>
      </Stack>
    </Stack>
  </>
);

const ReportTab = () => {
  const [token] = useRecoilState(tokenState);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sourceCodes, setSourceCodes] = useState([]);
  const [locationCodes, setLocationCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useReducer(
    (form, updates) => ({ ...form, ...updates }),
    {
      SourceCode: [],
      SourceUseDate: false,
      ticStaff: "",
      Security: "",
      SecurityUseDate: false,
      location: "",
      locationUseDate: false,
      accession: "",
      accessionUseDate: false,
      ChildlessUseDate: false,
    }
  );

  const handleChange = (fieldName: string, value: any) => {
    setForm({ [fieldName]: value });
  };

  const fetchJson = async (uri: string, query?: string) => {
    const url = `${environment.statisticsAPI}${uri}?${query ?? ""}`;

    const apiService = new APIService<any>(axios, new CacheService, url, token);
    const res = apiService.getAnyAsync().then((res: any) => { return res });

    return res;
  };

  const getReport = async (uri: string, query?: string) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${environment.statisticsAPI}${uri}?${query ?? ""}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const contentDisposition = response.headers.get("Content-Disposition") || "";
      let fileName = "downloaded-file";

      const match = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;]+)/i);

      if (match) {
        fileName = decodeURIComponent(match[1] || match[2] || match[3]);
      }

      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJson("GetSourceCodes").then(setSourceCodes).catch(console.error);
    fetchJson("GetLocationCodes").then(setLocationCodes).catch(console.error);
  }, []);

  const isValidDateRange = startDate && endDate && startDate < endDate;

  <Backdrop
    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
    open={loading}
  >
    <CircularProgress color="inherit" />
  </Backdrop>

  return (
    <div className="center margin-bottom">
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker label="Start Date" value={startDate} onChange={setStartDate} />
          <DatePicker label="End Date" value={endDate} onChange={setEndDate} />
        </LocalizationProvider>
      </Stack>

      <ReportSection
        title="Source Code Report"
        input={
          <Autocomplete
            multiple
            options={sourceCodes || []}
            getOptionLabel={(option) => option.Code}
            value={form.SourceCode}
            onChange={(event, newValue) =>
              handleChange("SourceCode", newValue)
            }
            isOptionEqualToValue={(option, value) => option.Code === value.Code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Source Code"
                variant="outlined"
                size="small"
                sx={{ width: 300 }}
              />
            )}
          />
        }
        useDate={
          <FormControlLabel
              control={
                <Checkbox
                  checked={form.SourceUseDate}
                  onChange={(e) => handleChange("SourceUseDate", e.target.checked)}
                  color="primary"
                />
              }
              label="Use Date?"
              disabled={!isValidDateRange}
            />
        }
        onDownload={() => {
            const queryParts = [];

            // Add source codes
            if (form.SourceCode.length > 0) {
              const codes = form.SourceCode
                .map((x) => `sourceCode=${encodeURIComponent(x.Code)}`)
                .join("&");
              queryParts.push(codes);
            }

            //Add date range if requested
            if (form.SourceUseDate && startDate && endDate 
              && startDate < endDate
            ) {
              queryParts.push(
                `startDate=${startDate}`,
                `endDate=${endDate}`
              );
            }

            const query = queryParts.join("&");
            getReport("GetSourceCodeReport", query);
          }
        }
        disabled={form.SourceCode.length === 0}
      />

      <ReportSection
        title="Storage Location Report"
        input={
          <TextField
            select
            label="Location Code"
            value={form.location}
            onChange={(e) => handleChange("location", e.target.value)}
            size="small"
            sx={{ width: 300 }}
          >
            {locationCodes.map((option) => (
              <MenuItem key={option.Code} value={option.Code}>
                {option.Code}
              </MenuItem>
            ))}
          </TextField>
        }
        useDate={
          <FormControlLabel
              control={
                <Checkbox
                  checked={form.LocationUseDate}
                  onChange={(e) => handleChange("LocationUseDate", e.target.checked)}
                  color="primary"
                />
              }
              label="Use Date?"
              disabled={!isValidDateRange}
            />
        }
        onDownload={() => {
            const queryParts = [];

            // Add Location
            queryParts.push(`location=${encodeURIComponent(form.location)}`);

            //Add date range if requested
            if (form.LocationUseDate && startDate && endDate 
              && startDate < endDate
            ) {
              queryParts.push(
                `startDate=${startDate}`,
                `endDate=${endDate}`
              );
            }

            const query = queryParts.join("&");
            getReport("GetStorageLocationReport", query);
          }
        }
        disabled={!form.location}
      />

      <ReportSection
        title="Security Report"
        input={
          <TextField
            select
            label="Security Type"
            value={form.Security}
            onChange={(e) => handleChange("Security", e.target.value)}
            size="small"
            sx={{ width: 300 }}
          >
            {["Public", "Restricted", "Sensitive", "Working"].map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        }
        useDate={
          <FormControlLabel
              control={
                <Checkbox
                  checked={form.SecurityUseDate}
                  onChange={(e) => handleChange("SecurityUseDate", e.target.checked)}
                  color="primary"
                />
              }
              label="Use Date?"
              disabled={!isValidDateRange}
            />
        }
        onDownload={() => {
            const queryParts = [];

            // Add Location
            if (form.Security.length > 0) {
              queryParts.push(`security=${encodeURIComponent(form.Security)}`);
            }

            //Add date range if requested
            if (form.SecurityUseDate && startDate && endDate 
              && startDate < endDate
            ) {
              queryParts.push(
                `startDate=${startDate}`,
                `endDate=${endDate}`
              );
            }

            const query = queryParts.join("&");
            getReport("GetSecurityReport", query);
          }
        }
        disabled={!form.Security}
      />

      <ReportSection 
        title="Accession Report" 
        input={
          <TextField          
            label="Accession Number"
            value={form.accession}
            onChange={(e) => handleChange("accession", e.target.value)}
            size="small"
            sx={{ width: 300 }}
          >
          </TextField>
        }
        useDate={
          <FormControlLabel
              control={
                <Checkbox
                  checked={form.accessionUseDate}
                  onChange={(e) => handleChange("accessionUseDate", e.target.checked)}
                  color="primary"
                />
              }
              label="Use Date?"
              disabled={!isValidDateRange}
            />
        }
        onDownload={() => {
            const queryParts = [];

            // Add Location
            queryParts.push(`accessionId=${encodeURIComponent(form.accession)}`);

            //Add date range if requested
            if (form.accessionUseDate && startDate && endDate 
              && startDate < endDate
            ) {
              queryParts.push(
                `startDate=${startDate}`,
                `endDate=${endDate}`
              );
            }

            const query = queryParts.join("&");
            getReport("GetAccessionReport", query);
          }
        }
        disabled={!form.accession} />

      <ReportSection
        title="Missing Attachment Report"
        useDate={
          <FormControlLabel
              control={
                <Checkbox
                  checked={form.ChildlessUseDate}
                  onChange={(e) => handleChange("ChildlessUseDate", e.target.checked)}
                  color="primary"
                />
              }
              label="Use Date?"
              disabled={!isValidDateRange}
            />
        }
        onDownload={() =>  {
          const queryParts = [];

          //Add date range if requested
          if (form.ChildlessUseDate && startDate && endDate 
            && startDate < endDate
          ) {
            queryParts.push(
              `startDate=${startDate}`,
              `endDate=${endDate}`
            );
          }

          const query = queryParts.join("&");
          getReport("GetChildlessReport", query);
        }}
      />

      <ReportSection
        title="TIC Staff Report"
        subtitle="* Uses Date"
        onDownload={() =>
          getReport(
            "GetStaffReport",
            `startDate=${startDate}&endDate=${endDate}`
          )
        }
        disabled={!isValidDateRange}
      />

    </div>
  );
};

export default ReportTab;
